/* =========================================================
   Bundle Builder — Biomiral
   ========================================================= */
(function () {
  'use strict';

  class BundleBuilder {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.id.replace('BundleBuilder-', '');
      this.cfg = (window.BundleBuilderConfig || {})[this.sectionId] || {};

      this.maxProducts    = this.cfg.maxProducts    || 5;
      this.minForDiscount = this.cfg.minForDiscount || 3;
      this.discounts      = this.cfg.discounts      || [];

      /* slots[i] = null | { productId, variantId, price, title, image } */
      this.slots = Array.from({ length: this.maxProducts }, () => null);

      this._initMobileDrawer();
      this._initAccordions();
      this._initCarousels();
      this._initAddButtons();
      this._initSlotRemove();
      this._initATC();
    }

    /* -------------------------------------------------------
       MOBILE DRAWER — panel slides up from bottom
    ------------------------------------------------------- */
    _initMobileDrawer() {
      const panel  = this.section.querySelector('[data-bundle-panel]');
      const header = this.section.querySelector('[data-panel-header]');
      if (!panel || !header) return;

      /* Only wire the toggle on mobile */
      const toggle = () => {
        if (window.innerWidth >= 990) return;
        panel.classList.toggle('bundle-panel--open');
      };

      header.addEventListener('click', toggle);

      /* Re-evaluate on resize so desktop never stays locked */
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 990) {
          panel.classList.remove('bundle-panel--open');
        }
      }, { passive: true });
    }

    /* Auto-open the drawer when a product is added on mobile */
    _openMobileDrawer() {
      if (window.innerWidth >= 990) return;
      const panel = this.section.querySelector('[data-bundle-panel]');
      if (panel) {
        panel.classList.add('bundle-panel--open');
        /* Re-render bar after panel animation (380ms) so scaleX se ve bien */
        setTimeout(() => this._updateTiers(), 400);
      }
    }

    /* -------------------------------------------------------
       ACCORDIONS
    ------------------------------------------------------- */
    _initAccordions() {
      const groups = this.section.querySelectorAll('[data-bundle-group]');

      groups.forEach((group) => {
        const toggle  = group.querySelector('[data-bundle-group-toggle]');
        const content = group.querySelector('.bundle-group__content');
        if (!toggle || !content) return;

        toggle.addEventListener('click', () => {
          const isOpen = !content.hidden;

          /* close all */
          groups.forEach((g) => {
            g.classList.remove('bundle-group--open');
            const c = g.querySelector('.bundle-group__content');
            const t = g.querySelector('[data-bundle-group-toggle]');
            if (c) c.hidden = true;
            if (t) t.setAttribute('aria-expanded', 'false');
          });

          /* open the clicked one unless it was already open */
          if (!isOpen) {
            group.classList.add('bundle-group--open');
            content.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }

    /* -------------------------------------------------------
       CAROUSELS
    ------------------------------------------------------- */
    _initCarousels() {
      this.section.querySelectorAll('[data-bundle-carousel]').forEach((carouselEl) => {
        const track   = carouselEl.querySelector('[data-carousel-track]');
        const prevBtn = carouselEl.querySelector('[data-carousel-prev]');
        const nextBtn = carouselEl.querySelector('[data-carousel-next]');
        if (!track) return;

        const state = { index: 0, perView: this._perView() };

        const render = () => {
          const items = Array.from(track.children);
          if (!items.length) return;

          const itemW = items[0].getBoundingClientRect().width;
          const gap   = 16;
          track.style.transform = `translateX(-${state.index * (itemW + gap)}px)`;

          const total = items.length;
          if (prevBtn) prevBtn.disabled = state.index === 0;
          if (nextBtn) nextBtn.disabled = state.index >= total - state.perView;
        };

        const clamp = () => {
          const total = track.children.length;
          state.perView = this._perView();
          state.index   = Math.min(state.index, Math.max(0, total - state.perView));
        };

        prevBtn?.addEventListener('click', () => {
          if (state.index > 0) { state.index--; render(); }
        });

        nextBtn?.addEventListener('click', () => {
          const total = track.children.length;
          if (state.index < total - state.perView) { state.index++; render(); }
        });

        const ro = new ResizeObserver(() => { clamp(); render(); });
        ro.observe(carouselEl);

        requestAnimationFrame(render);
      });
    }

    _perView() {
      const w = window.innerWidth;
      if (w < 480) return 2;
      if (w < 768) return 2;
      return 4;
    }

    /* -------------------------------------------------------
       ADD PRODUCT TO BUNDLE
    ------------------------------------------------------- */
    _initAddButtons() {
      this.section.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-bundle-add]');
        if (!btn) return;

        const card = btn.closest('[data-product-card]');
        if (!card) return;

        const productId = card.dataset.productId;

        if (this._isInBundle(productId)) {
          this._removeByProductId(productId);
          return;
        }

        const emptyIdx = this.slots.findIndex((s) => s === null);
        if (emptyIdx === -1) return;

        this.slots[emptyIdx] = {
          productId:  card.dataset.productId,
          variantId:  card.dataset.variantId,
          price:      card.dataset.price,
          title:      card.dataset.title,
          image:      card.dataset.image,
        };

        this._renderSlot(emptyIdx);
        this._markCard(productId, true);
        this._updateCount();
        this._updateTiers();
        this._updateATC();
        this._refreshAllButtons();
        this._openMobileDrawer();
      });
    }

    _isInBundle(productId) {
      return this.slots.some((s) => s && s.productId === productId);
    }

    _removeByProductId(productId) {
      const idx = this.slots.findIndex((s) => s && s.productId === productId);
      if (idx === -1) return;
      this.slots[idx] = null;
      this._renderSlot(idx);
      this._markCard(productId, false);
      this._updateCount();
      this._updateTiers();
      this._updateATC();
      this._refreshAllButtons();
    }

    /* -------------------------------------------------------
       REMOVE FROM SLOT
    ------------------------------------------------------- */
    _initSlotRemove() {
      const slotsWrap = this.section.querySelector('[data-bundle-slots]');
      if (!slotsWrap) return;

      slotsWrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-slot-remove]');
        if (!btn) return;

        const slotEl = btn.closest('[data-slot-index]');
        if (!slotEl) return;

        const idx  = parseInt(slotEl.dataset.slotIndex, 10);
        const slot = this.slots[idx];
        if (!slot) return;

        const { productId } = slot;
        this.slots[idx] = null;

        this._renderSlot(idx);
        this._markCard(productId, false);
        this._updateCount();
        this._updateTiers();
        this._updateATC();
        this._refreshAllButtons();
      });
    }

    /* -------------------------------------------------------
       RENDER HELPERS
    ------------------------------------------------------- */
    _renderSlot(idx) {
      const slotEl  = this.section.querySelector(`[data-slot-index="${idx}"]`);
      if (!slotEl) return;

      const emptyEl  = slotEl.querySelector('[data-slot-empty]');
      const filledEl = slotEl.querySelector('[data-slot-filled]');
      const slot     = this.slots[idx];

      if (slot) {
        if (emptyEl)  emptyEl.hidden  = true;
        if (filledEl) {
          filledEl.hidden = false;
          const img   = filledEl.querySelector('[data-slot-img]');
          const title = filledEl.querySelector('[data-slot-title]');
          const price = filledEl.querySelector('[data-slot-price]');
          if (img)   { img.src = slot.image || ''; img.alt = slot.title || ''; }
          if (title) { title.textContent = slot.title || ''; }
          if (price) { price.textContent = this._money(parseInt(slot.price || 0, 10)); }
        }
      } else {
        if (emptyEl)  emptyEl.hidden  = false;
        if (filledEl) filledEl.hidden = true;
      }
    }

    _markCard(productId, added) {
      this.section
        .querySelectorAll(`[data-product-card][data-product-id="${productId}"]`)
        .forEach((c) => c.classList.toggle('is-added', added));
    }

    _refreshAllButtons() {
      const full = this.slots.every((s) => s !== null);

      this.section.querySelectorAll('[data-bundle-add]').forEach((btn) => {
        const card = btn.closest('[data-product-card]');
        if (!card) return;
        const pid      = card.dataset.productId;
        const inBundle = this._isInBundle(pid);

        if (inBundle) {
          btn.textContent = this.cfg.removeText || 'Eliminar';
          btn.disabled    = false;
        } else {
          btn.textContent = this.cfg.addBtnText || 'Agregar';
          btn.disabled    = full;
        }
      });
    }

    _updateCount() {
      const n  = this._filledCount();
      const el = this.section.querySelector('[data-bundle-count]');
      if (el) el.textContent = `${n}/${this.maxProducts}`;
    }

    _updateTiers() {
      const n   = this._filledCount();
      const pct = (n / this.maxProducts) * 100;

      const bar = this.section.querySelector('[data-tier-progress]');
      if (bar) bar.style.width = pct + '%';

      /* % labels */
      this.section.querySelectorAll('[data-tier-pct]').forEach((el) => {
        const tier = this.discounts[parseInt(el.dataset.tierPct, 10) - 1];
        if (tier) el.classList.toggle('is-active', n >= tier.qty);
      });

      /* tick marks */
      this.section.querySelectorAll('[data-tier-tick]').forEach((el) => {
        const tier = this.discounts[parseInt(el.dataset.tierTick, 10) - 1];
        if (tier) el.classList.toggle('is-active', n >= tier.qty);
      });

      /* qty labels */
      this.section.querySelectorAll('[data-tier-qty]').forEach((el) => {
        const tier = this.discounts[parseInt(el.dataset.tierQty, 10) - 1];
        if (tier) el.classList.toggle('is-active', n >= tier.qty);
      });

      /* discount badge */
      const badge     = this.section.querySelector('[data-discount-badge]');
      const badgeText = this.section.querySelector('[data-discount-text]');
      const discount  = this._currentDiscount(n);

      if (badge && badgeText) {
        if (discount > 0) {
          badgeText.textContent = (this.cfg.discountText || '¡Descuento del [X]% desbloqueado!')
            .replace('[X]', discount);
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    }

    _currentDiscount(qty) {
      let pct = 0;
      this.discounts.forEach((d) => { if (qty >= d.qty) pct = d.pct; });
      return pct;
    }

    _updateATC() {
      const btn = this.section.querySelector('[data-bundle-atc]');
      if (btn) btn.disabled = this._filledCount() < this.minForDiscount;
    }

    _filledCount() {
      return this.slots.filter((s) => s !== null).length;
    }

    /* -------------------------------------------------------
       ADD TO CART
    ------------------------------------------------------- */
    _initATC() {
      const btn = this.section.querySelector('[data-bundle-atc]');
      if (!btn) return;

      btn.addEventListener('click', async () => {
        if (btn.disabled) return;

        const filled = this.slots.filter(Boolean);
        if (!filled.length) return;

        const textEl = btn.querySelector('[data-atc-text]') || btn;
        const orig   = textEl.textContent;

        btn.disabled = true;
        btn.classList.add('is-loading');
        textEl.textContent = this.cfg.atcLoadingText || 'Agregando...';

        const items = filled.map((s) => ({
          id:       parseInt(s.variantId, 10),
          quantity: 1,
        }));

        try {
          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ items }),
          });

          if (!res.ok) throw new Error('cart/add failed');

          textEl.textContent = this.cfg.atcSuccessText || '¡Agregado!';
          btn.classList.remove('is-loading');

          fetch('/cart.js')
            .then((r) => r.json())
            .then((cart) => {
              document.querySelectorAll(
                '[data-cart-count], .cart-count-bubble span, #CartCount'
              ).forEach((el) => { el.textContent = cart.item_count; });

              document.dispatchEvent(
                new CustomEvent('bundle:added-to-cart', {
                  bubbles: true,
                  detail: { items, cart },
                })
              );
            })
            .catch(() => {});

          setTimeout(() => {
            textEl.textContent = orig;
            btn.disabled = this._filledCount() < this.minForDiscount;
          }, 2200);

        } catch (err) {
          console.error('[BundleBuilder] cart/add error:', err);
          btn.classList.remove('is-loading');
          textEl.textContent = 'Error. Intenta de nuevo.';
          setTimeout(() => {
            textEl.textContent = orig;
            btn.disabled = false;
          }, 2500);
        }
      });
    }

    /* -------------------------------------------------------
       UTILITIES
    ------------------------------------------------------- */
    _money(cents) {
      return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
  }

  /* ---------------------------------------------------------
     Boot
  --------------------------------------------------------- */
  function boot() {
    document.querySelectorAll('[id^="BundleBuilder-"]').forEach((el) => {
      if (!el._bbInit) {
        el._bbInit = true;
        new BundleBuilder(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
